import { Helmet } from "@dr.pogodin/react-helmet";
import ResourceCard from "../components/ResourceCard";

export const meta = {
  slug: "home-office",
  title: "Home Office Deductions for 1099 Workers",
  description: "Discover how to qualify for and calculate the home office deduction as a self-employed 1099 contractor.",
  date: "2025-01-11",
  tags: ["home office", "deductions", "tax savings"]
};

const otherResources = [
  { title: "Do You Need to Pay Quarterly Taxes?", slug: "do-you-need-to-pay-quarterly-taxes" },
  { title: "Home Office Deductions for 1099 Workers", slug: "home-office" },
  {
    title: "How to Calculate What You Owe as a 1099 Contractor",
    slug: "how-to-calculate-what-you-owe-1099",
  },
  { title: "HSA Deductions for 1099 Workers", slug: "hsa-deductions" },
  { title: "Mileage Deduction Rules for 1099 Contractors", slug: "mileage-deduction" },
  { title: "Who Should Use a 1099 Calculator?", slug: "who-should-use-1099-calculator" },
];

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export default function HomeOffice() {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <Helmet>
        <title>Home Office Deductions for 1099 Workers | Free1099Calc</title>
        <meta
          name="description"
          content="Discover how to qualify for and calculate the home office deduction as a self-employed 1099 contractor."
        />
      </Helmet>

      <>
        <h1 className="text-3xl font-bold mb-4">Home Office Deductions for 1099 Workers</h1>
        <p>
          If you use a portion of your home exclusively for business, the IRS may allow you to
          deduct related expenses even rent or utilities.
        </p>

        <h2 className="text-2xl font-semibold mt-6 mb-3">What Qualifies as a Home Office?</h2>
        <ul className="list-disc ml-6 mb-4">
          <li>
            A dedicated space used <strong>exclusively</strong> for business.
          </li>
          <li>Used regularly and not shared with personal activities.</li>
        </ul>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
          <strong>Tip:</strong> Even a small corner of your home may qualify if used solely for your
          business.
        </div>

        <h2 className="text-2xl font-semibold mt-6 mb-3">Simplified vs. Regular Method</h2>
        <ol className="list-decimal ml-6 mb-4">
          <li>
            <strong>Simplified:</strong> \ per sq ft (up to 300 sq ft).
          </li>
          <li>
            <strong>Regular:</strong> Actual expenses business-use percentage.
          </li>
        </ol>
        <hr className="my-8" />
        <h3 className="text-xl font-bold mb-4">Read Other Resources</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {shuffle(otherResources)
            .filter((r) => r.title !== "Home Office Deductions for 1099 Workers")
            .map((r) => (
              <ResourceCard key={r.slug} title={r.title} link={"/resources/" + r.slug} />
            ))}
        </div>
      </>
    </div>
  );
}
